import * as React from 'react'

import dynamic from 'next/dynamic'

import { spacing, styled } from '@thg-commerce/gravity-theme'
import { VisuallyHidden } from '@thg-commerce/gravity-elements/VisuallyHidden'

import { RatingSize } from '../ProductReview'
import { LabelText } from '../../../FormItem/Label'
import ErrorMessage from '../../../ErrorMessage'

const Star = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/ReviewStar'),
)

export interface StarRatingPickerProps {
  starRating?: string
  onChangeRating?: (number) => void
  screenReaderOnlyText: string
  size?: RatingSize
  bindref?: any
  required?: boolean
  label: string
  error?: string | boolean
}

const StarContainer = styled.div`
  display: flex;
  align-content: center;
  flex-direction: row;
  column-gap: 12px;
  pointer-events: none;

  &:hover {
    svg {
      path {
        fill: #f2b35a;
      }
    }
  }

  :focus-within {
    svg {
      path {
        fill: #f2b35a;
      }
    }
    button:focus ~ button {
      svg {
        path {
          fill: none;
        }
      }
    }
  }
`

const IconButton = styled.button`
  pointer-events: auto;
  &:hover ~ button {
    svg {
      path {
        fill: none;
      }
    }
  }
  &:focus-visible ~ button {
    svg {
      path {
        fill: none;
      }
    }
  }
  &:focus-visible {
    outline: solid 2px ${(props) => props.theme.colors.palette.brand.base};
  }
`
const StyledLabelText = styled(LabelText)<{
  required?: boolean
}>`
  ${(props) => !props.required && `padding-left: ${spacing(1)};`}
  margin-bottom:${spacing(1)};
`

export const StarRatingPicker = (props: StarRatingPickerProps) => {
  React.useImperativeHandle(props.bindref, () => ({
    ...(props as StarRatingPickerProps & HTMLButtonElement),
    validate: (): boolean | undefined => {
      const valid = props.required && ratingStarsState !== ''
      if (!valid && starsEl !== null && starsEl.current !== null) {
        setError(`${props.label} is a required field`)
      }
      return valid
    },
    value: ratingStarsState,
  }))

  const requiredProps = {
    required: props.required,
    'aria-required': props.required,
  }

  const starsEl: React.RefObject<HTMLInputElement> =
    props.bindref || React.useRef<HTMLInputElement>(null)

  const [ratingStarsState, setRatingStarsState] = React.useState(
    starsEl !== null && starsEl.current !== null
      ? starsEl.current.value
      : props.starRating || '',
  )

  const [error, setError] = React.useState<string | boolean>(false)

  React.useEffect(() => {
    if (props.error) {
      setError(props.error)
    }
  }, [props.error])
  const stars = Array.from({ length: 5 }, (_, index) => {
    return (
      <IconButton
        type={'button'}
        key={index}
        onClick={() => {
          setRatingStarsState(String(index + 1))
          setError(false)
        }}
        data-testid={`rating-star-${index}`}
      >
        <Star
          height={props.size === RatingSize.Large ? '30px' : '16px'}
          width={props.size === RatingSize.Large ? '30px' : '16px'}
          fill={
            ratingStarsState && String(index + 1) <= ratingStarsState
              ? '#D77F04'
              : 'transparent'
          }
        />
      </IconButton>
    )
  })

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: `${spacing(4)}`,
      }}
    >
      <StyledLabelText required={props.required}>{props.label}</StyledLabelText>
      <input
        type={`hidden`}
        value={ratingStarsState}
        ref={starsEl}
        {...requiredProps}
        onChange={(_: React.ChangeEvent<HTMLInputElement>) => {
          if (starsEl !== null && starsEl.current !== null) {
            setRatingStarsState(starsEl.current.value)
          }
        }}
      />
      <VisuallyHidden text={props.screenReaderOnlyText} />
      <StarContainer aria-hidden>{stars}</StarContainer>
      {error && <ErrorMessage id={`rating-stars-error`} error={error} />}
    </div>
  )
}
